import React, { useEffect, useState } from 'react';
import { after, map } from 'lodash';
import dayjs from 'dayjs';

import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Fab from '@material-ui/core/Fab';
import blue from '@material-ui/core/colors/blue';
import { makeStyles } from '@material-ui/core/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { Button, LoaderSpinner } from '../../components';
import { COUNTRIES } from '../../constants';
import { validateName, validateNationalNo, validateStageName, validateDate, validateCountry } from '../../utils/generalUtils'; // Update the import path to match your file structure

import '../NewCustomer/NewCustomer.scss';
import './ArtistDetailsProfile.scss';
import { artistActions } from '../../actions';
import { _showAlert } from '../../utils';

const useStyles = makeStyles({
  cancelButton: {
    borderRadius: 46,
    background: '#40189D',
    boxShadow: 'none',
    padding: '12px 28px',
    color: 'white',
    borderColor: 'transparent',
    cursor: 'pointer',
  },

  cardContainer: {
    width: '100px',
    margin: '10px',
  },
  input: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    zIndex: 10,
    cursor: 'pointer',
  },
  button: {
    color: blue[900],
    margin: 10,
    position: 'absolute',
    bottom: 0,
    left: 0,
    borderRadius: 6,
    width: 60,
    height: 60,
    pointerEvents: 'none',
  },
  cardactionarea: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    borderRadius: 24,
    width: 60,
    height: 60,
    overflow: 'hidden',
    zIndex: 11,
  },
  logo: {
    width: '100px',
    height: '100px',
  },
  submit: {
    width: '120px',
  },
});

function ArtistDetailsProfile(props) {
  const { artist, setArtist } = props;

  const classes = useStyles();
  const [uploadState, setUploadState] = React.useState('initial');
  const [formFields, setFormFields] = useState({
    country: '',
  });
  const [errors, setErrors] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  const handleUploadClick = (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    const reader = new FileReader();
    if (file) {
      reader.readAsDataURL(file);
      reader.onloadend = function (e) {
        setFormFields({
          ...formFields,
          profile_img: {
            file: file,
            preview: reader.result,
          },
        });
        setUploadState('uploaded');
      };
    }
  };

  const handleResetClick = (event) => {
    setFormFields({
      ...formFields,
      profile_img: '',
    });
    setUploadState('initial');
  };

  const handleInputChange = (event) => {
    let id = event.target.id;
    let value = event.target.value;
    setFormFields({ ...formFields, [id]: value });
    setErrors({ ...errors, [id]: '' });
  };

  const uploadAvatar = async (file) => {
    setIsLoading(true);
    try {
      // Step 1: Get Signed URL
      const signedUrlResponse = await artistActions.getAvatarUploadUrl(artist.id, {
        filename: file.name,
        mimetype: file.type || 'image/jpeg',
      });

      if (!signedUrlResponse || signedUrlResponse.code !== 'OK') {
        throw new Error('Failed to get upload authorization for avatar.');
      }

      const { signed_url, key } = signedUrlResponse.data;

      // Step 2: Upload to R2
      const uploadResponse = await fetch(signed_url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'image/jpeg',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload avatar to storage.');
      }

      // Step 3: Save Record
      const saveResult = await artistActions.uploadAvatar(artist.id, {
        avatar_key: key,
      });

      if (saveResult) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      _showAlert(error.message || 'Error uploading avatar. Please try again.', 'error', 4000);
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // Check which fields are different in formfields and artist
    const changedFields = Object.keys(formFields).filter((key) => formFields[key] !== artist[key]);
    const changedData = {};
    changedFields.forEach((key) => {
      if (key !== 'profile_img') {
        changedData[key] = formFields[key];
      } else {
        uploadAvatar(formFields[key].file);
      }
    });

    const nameError = validateName(formFields.first_name);
    const nationalNoError = validateNationalNo(formFields.national_identity_no);
    const stageNameError = validateStageName(formFields.stage_name);
    const dateError = validateDate(new Date(formFields.birthday));
    const countryError = validateCountry(formFields.country);

    setErrors({
      first_name: nameError,
      national_identity_no: nationalNoError,
      stage_name: stageNameError,
      birthday: dateError,
      country: countryError,
    });

    // If there are no validation errors, proceed with saving the data
    if (!nameError && !nationalNoError && !stageNameError && !dateError && !countryError) {
      if (Object.keys(changedData).length !== 0) {
        setIsLoading(true);
        const res = await artistActions.updateArtist(artist.id, changedData);
        if (res.success) {
          setArtist(res.data);
          setIsChanged(false);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setErrors(res.errors);
        }
      }
    }
  };

  useEffect(() => {
    setFormFields(artist);
  }, [artist]);

  useEffect(() => {
    // check if any values from formfields is defffereent from artist
    const isChanged = Object.keys(formFields).some((key) => formFields[key] !== artist[key]);
    setIsChanged(isChanged);
  }, [formFields]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
      className="new_customer_row"
    >
      <h5>Artist profile</h5>
      <div className="field_row">
        <div className="field_col">
          <label>Username</label>
          <input type="text" value={formFields.username} id="username" disabled />
        </div>
        <div className="field_col">
          <label>Name</label>
          <input type="text" value={formFields.first_name} onChange={handleInputChange} id="first_name" />
          <span className="input-error-messages">{errors && errors['first_name']}</span>
        </div>
        <div className="field_col">
          <label>Email</label>
          <input type="text" value={formFields.email} id="email" disabled />
        </div>
        <div className="field_col">
          <label>Phone Number</label>

          <input type="text" value={formFields.phone_no} id="phone_no" disabled />
        </div>
        <div className="field_col">
          <label>D.O.B</label>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  width: '100%',
                  border: '0',
                  borderRadius: '0',
                  borderBottom: '1px solid #blue',
                },

                '& .MuiInputBase-input': {
                  padding: 0,
                },
              }}
              value={dayjs(formFields.birthday)}
              onChange={(e) => {
                try {
                  const jsDateObject = dayjs(e).toDate().toISOString();
                  handleInputChange({ target: { id: 'birthday', value: jsDateObject } });
                } catch (error) {
                  console.error('Error parsing date:', error);
                }
              }}
            />
          </LocalizationProvider>
          <span className="input-error-messages">{errors && errors['birthday']}</span>
        </div>

        <div className="field_col">
          <FormControl variant="standard" sx={{ width: '100%' }}>
            <label>Country</label>
            <Select
              labelId="demo-simple-select-standard-label"
              id="country"
              value={formFields.country}
              onChange={(e) => handleInputChange({ target: { id: 'country', value: e.target.value } })}
              label="Country"
              style={{ margin: 0, height: '40px' }}
              // set the height of itemcontianer to 300px
            >
              {map(COUNTRIES, (i) => (
                <MenuItem value={i.value}>{i.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className="field_col">
          <label className={uploadState !== 'uploaded' ? classes.cardRoot : classes.cardRootHide}>Profile Photo</label>
          <div style={{ position: 'relative' }}>
            <input
              accept="image/jpeg,image/png,image/tiff,image/webp"
              className={classes.input}
              id="contained-button-file"
              name="logo"
              type="file"
              onChange={handleUploadClick}
            />
            {artist.profile_img && (
              <Fab
                component="span"
                style={{ backgroundImage: `url(${process.env.REACT_APP_MEDIA_URL}/images/${artist.profile_img})`, backgroundSize: 'cover' }}
                className={classes.button}
              ></Fab>
            )}
            <input type="text" value={formFields.business_name} onChange={handleInputChange} id="business_name" />

            {uploadState === 'uploaded' && (
              <img
                style={{ width: '60px', height: '60px' }}
                src={formFields.profile_img?.preview}
                alt="LOGO"
                className={classes.cardactionarea}
                onClick={handleResetClick}
              />
            )}
          </div>
        </div>

        <div className="field_col">
          <label>National I’D Number</label>
          <input type="text" value={formFields.national_identity_no} onChange={handleInputChange} id="national_identity_no" />
          <span className="input-error-messages">{errors && errors['national_identity_no']}</span>
        </div>

        <div className="field_col">
          <label>Stage Name</label>
          <input type="text" value={formFields.stage_name} onChange={handleInputChange} id="stage_name" />
          <span className="input-error-messages">{errors && errors['stage_name']}</span>
        </div>
      </div>

      <Button isLoading={isLoading} className="round_btn" disabled={!isChanged}>
        Save Changes
      </Button>
    </form>
  );
}

export default ArtistDetailsProfile;

